import type { StateFinding } from '@/lib/apiTypes'
import { SEVERITY_RANK } from '@/lib/format'
import { resourceTypeOf } from '@/features/state/lib/tfAddress'

/**
 * Normalising state findings for a single table.
 *
 * The four state scanners put DIFFERENT things in the same fields, so a table cannot be built by
 * naming columns after fields:
 *
 *   category            title is…                 resource is…           description is…
 *   secret              detector name             TF address             the attribute key
 *   exposure·endpoint   a bare host               TF address             the attribute key
 *   exposure·port       "Open ports: 22, 443"     a HOSTNAME, not an     null
 *                                                 address
 *   misconfiguration    rule title                TF address             (no producer today)
 *
 * So columns are named after the question they answer - what / where / what specifically - and
 * each category answers them its own way. `viewOf` is the single place that mapping lives.
 *
 * Everything here is pure data: `detail` is a tagged union rather than a ReactNode so this module
 * stays plain TypeScript and the grouping can be unit-tested without rendering.
 */

/** Categories with their own tab in the filter. Anything else folds into `misconfigs`, which is
 *  the catch-all - `malware` and `vulnerability` are valid FindingCategory values with no state
 *  producer today, and a finding must never silently vanish from the table. */
const KNOWN_FINDING_CATEGORIES = new Set<StateFinding['category']>(['secret', 'misconfiguration', 'exposure'])

export type CategoryFilter = 'all' | 'secrets' | 'misconfigs' | 'exposure'

export const CATEGORY_FILTERS: CategoryFilter[] = ['all', 'secrets', 'misconfigs', 'exposure']

/** The Detail cell's content, as data. The component decides how to paint it. */
export type FindingDetail =
  | { kind: 'text'; value: string | null }
  | { kind: 'endpoint'; address: string; isPrivate: boolean; resolvedIp: string | null; unresolved: boolean }
  | { kind: 'ports'; ports: number[] }

export interface FindingView {
  /** Which filter bucket this finding falls in. */
  bucket: Exclude<CategoryFilter, 'all'>
  /** Shown in the Category column, e.g. `exposure · endpoint`. */
  categoryLabel: string
  /** The Finding cell: WHAT was found. */
  what: string
  /** The Location cell: WHERE it was found. A TF address, or a host for port findings. */
  where: string | null
  /** Sub-line under Location. Null unless `where` really is a TF address - a port finding's
   *  resource is a hostname, and the address parser cannot reject one structurally
   *  (`api.example.com` would yield `example`), so this is decided by category, not by parsing. */
  whereType: string | null
  detail: FindingDetail
}

/** The attribute a finding sits in: the structured `meta.attr` the secret tools write, else
 *  `description`, which is where the endpoint tool puts the same thing. */
export function findingAttribute(f: StateFinding): string | null {
  const attr = f.meta?.['attr']
  if (typeof attr === 'string' && attr) return attr
  return f.description || null
}

function str(v: unknown): string | null {
  return typeof v === 'string' && v ? v : null
}

/** Ports, guarded on ELEMENT type: Array.isArray alone would render "[object Object]" if the
 *  scanner ever enriched ports to {port, proto}. */
function portsOf(f: StateFinding): number[] {
  const raw = f.meta?.['ports']
  return Array.isArray(raw) ? (raw as unknown[]).filter((v): v is number => typeof v === 'number') : []
}

export function viewOf(f: StateFinding): FindingView {
  const subtype = str(f.meta?.['subtype'])

  if (f.category === 'exposure' && subtype === 'port') {
    const ports = portsOf(f)
    return {
      bucket: 'exposure',
      categoryLabel: 'exposure · port',
      // Built from the port count, not from `title`: the scanner ships a prebuilt sentence
      // ("Open ports: 22, 443") that duplicates the Detail cell beside it.
      what: `${ports.length} open port${ports.length === 1 ? '' : 's'}`,
      where: f.resource,
      whereType: null, // a hostname, not an address
      detail: { kind: 'ports', ports },
    }
  }

  if (f.category === 'exposure' && subtype === 'endpoint') {
    return {
      bucket: 'exposure',
      categoryLabel: 'exposure · endpoint',
      what: f.title, // the bare host
      where: f.resource,
      whereType: resourceTypeOf(f.resource),
      detail: {
        kind: 'endpoint',
        // meta.url is null for a bare host with no scheme; the title IS that host.
        address: str(f.meta?.['url']) ?? f.title,
        isPrivate: f.meta?.['private'] === true,
        resolvedIp: str(f.meta?.['resolved_ip']),
        unresolved: f.meta?.['unresolved'] === true,
      },
    }
  }

  const bucket: Exclude<CategoryFilter, 'all'> =
    f.category === 'secret' ? 'secrets'
      : f.category === 'exposure' ? 'exposure'
        : 'misconfigs'

  return {
    bucket,
    categoryLabel: f.category === 'secret' ? 'secret'
      : f.category === 'exposure' ? 'exposure'
        : KNOWN_FINDING_CATEGORIES.has(f.category) ? 'misconfig'
          // An unmapped category keeps its own name rather than being relabelled "misconfig":
          // it still filters under Misconfigs, but the cell should not lie about what it is.
          : String(f.category ?? 'unknown'),
    what: f.title,
    where: f.resource,
    whereType: resourceTypeOf(f.resource),
    detail: { kind: 'text', value: findingAttribute(f) },
  }
}

export interface FindingGroup {
  key: string
  severity: string
  /** The view of the group's first occurrence; the shared columns render from this. */
  view: FindingView
  occurrences: StateFinding[]
  /** Occurrences disagree about `where` / `detail`, so the cell shows the first plus "+N". */
  whereVaries: boolean
  detailVaries: boolean
}

/** Stable identity for "is this the same finding, seen again elsewhere". */
function groupKey(f: StateFinding, v: FindingView): string {
  // severity is IN the key on purpose. Trufflehog sets critical-vs-high purely on `Verified`
  // while keeping DetectorName as both rule_id and title, so one detector legitimately emits
  // both. Splitting them beats reporting a group maximum: a verified live credential is a
  // different triage item from an unverified hit, and the row's badge stays exactly true.
  // rule_id is in the key too - grouping on title alone (the old secrets table) merged distinct
  // rules that happen to share a title.
  return [f.category, str(f.meta?.['subtype']) ?? '', f.rule_id ?? v.what, f.title, f.severity].join('|')
}

function detailFingerprint(d: FindingDetail): string {
  switch (d.kind) {
    case 'text': return `t:${d.value ?? ''}`
    case 'ports': return `p:${d.ports.join(',')}`
    case 'endpoint': return `e:${d.address}|${d.resolvedIp ?? ''}|${d.isPrivate}|${d.unresolved}`
  }
}

/**
 * Collapse findings into table rows, worst first.
 *
 * This is PRESENTATION grouping, not de-duplication: cross-tool duplicates are already collapsed
 * server-side by the scanner's dedupeFindings. The point here is that one rule firing on twenty
 * resources is one row with a count of twenty, not twenty rows.
 */
export function groupFindings(findings: StateFinding[]): FindingGroup[] {
  const byKey = new Map<string, FindingGroup>()
  const wheres = new Map<string, Set<string>>()
  const details = new Map<string, Set<string>>()

  for (const f of findings) {
    const view = viewOf(f)
    const key = groupKey(f, view)
    let g = byKey.get(key)
    if (!g) {
      g = { key, severity: f.severity, view, occurrences: [], whereVaries: false, detailVaries: false }
      byKey.set(key, g)
      wheres.set(key, new Set())
      details.set(key, new Set())
    }
    g.occurrences.push(f)
    wheres.get(key)!.add(view.where ?? '')
    details.get(key)!.add(detailFingerprint(view.detail))
  }

  for (const [key, g] of byKey) {
    g.whereVaries = (wheres.get(key)?.size ?? 0) > 1
    g.detailVaries = (details.get(key)?.size ?? 0) > 1
  }

  return [...byKey.values()].sort((a, b) =>
    (SEVERITY_RANK[b.severity] ?? 0) - (SEVERITY_RANK[a.severity] ?? 0) ||
    b.occurrences.length - a.occurrences.length ||
    a.view.what.localeCompare(b.view.what))
}

/** Segment counts, computed over GROUPS so they match the rows the table will show. */
export function categoryCounts(groups: FindingGroup[]): Record<CategoryFilter, number> {
  const counts: Record<CategoryFilter, number> = { all: groups.length, secrets: 0, misconfigs: 0, exposure: 0 }
  for (const g of groups) counts[g.view.bucket]++
  return counts
}

export function filterGroups(groups: FindingGroup[], filter: CategoryFilter): FindingGroup[] {
  return filter === 'all' ? groups : groups.filter((g) => g.view.bucket === filter)
}

/** Verification state of a secret occurrence. verified===true is a confirmed live credential;
 *  a verification_error means we could not reach the provider (unknown, not dead). */
export function readVerification(meta: Record<string, unknown> | null): 'live' | 'failed' | null {
  if (!meta) return null
  if (meta['verified'] === true) return 'live'
  if (str(meta['verification_error'])) return 'failed'
  return null
}

/** Trufflehog's rotation guidance, carried in meta and rendered nowhere until now. */
export function rotationGuide(f: StateFinding): string | null {
  return str(f.meta?.['rotation_guide'])
}
